import { TeContainerSlim, TeIcon, TePopover, TeText } from '@topcon/tuicl-react';

import ScoreIndicator from './ScoreIndicator';
import { MigrationCandidateMatchTarget } from '../../api/generated/pm';

/**
 * Already-translated copy for the badge and its tooltip. Passed in rather than
 * resolved here so the badge stays scope-agnostic — the identity scope reads
 * `companies:pendingMerges`, the dealer scope `common:customerSuggestions`.
 */
interface MatchScoreLabels {
  /** e.g. "95% match" — already interpolated for this target. */
  score: string;
  address: string;
  businessName: string;
  domain: string;
}

interface TooltipRowProps {
  label: string;
  score: number;
}

function TooltipRow({ label, score }: TooltipRowProps) {
  return (
    <TeContainerSlim gap="xs" alignItems="center" justifyContent="space-between" fill>
      <TeText size={12} color="text.inverse">
        {label}
      </TeText>
      <ScoreIndicator score={score} />
    </TeContainerSlim>
  );
}

interface Props {
  target: MigrationCandidateMatchTarget;
  selected: boolean;
  labels: MatchScoreLabels;
}

/**
 * Corner badge on a potential-match row: the overall match percentage plus a
 * hover tooltip breaking the score down per dimension.
 */
function MatchScoreBadge({ target, selected, labels }: Props) {
  const popkey = `match-badge-${target.external_id}`;

  return (
    <div style={{ position: 'absolute', top: 0, right: 0 }} data-popkey={popkey}>
      <TeContainerSlim
        backgroundColor={selected ? 'background.brand.primary' : 'background.brand.secondary'}
        padding={['xs3', 'xs']}
        gap="xs2"
        alignItems="center"
        style={{ borderBottomLeftRadius: 4, borderTopRightRadius: 4 }}
      >
        <TeText size={12} color="text.inverse">
          {labels.score}
        </TeText>
        <TeIcon name="info" size="s" color="icon.inverse" />
      </TeContainerSlim>
      <TePopover
        for={popkey}
        operation="hover"
        variant="secondary"
        placement="bottomRight"
        arrowless={false}
      >
        <TeContainerSlim column gap="xs2" padding="xs" style={{ minWidth: 212 }}>
          <TooltipRow label={labels.address} score={target.address_score} />
          <TooltipRow label={labels.businessName} score={target.name_score} />
          <TooltipRow label={labels.domain} score={target.domain_score} />
        </TeContainerSlim>
      </TePopover>
    </div>
  );
}

export default MatchScoreBadge;
export type { MatchScoreLabels };
